import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Landmark, Star } from "lucide-react";

const empty = { bank_name: "", account_number: "", ifsc: "", holder_name: "", branch: "", upi_id: "", is_default: false };

export default function Banks() {
  const { user } = useAuth();
  const [banks, setBanks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/banks");
    setBanks(data);
  };
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/banks/${editing.id}`, form);
      else await api.post("/banks", form);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this bank?")) return;
    await api.delete(`/banks/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="p-6 md:p-8 max-w-[1400px]">
      <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="label-caps mb-2">Money</p>
          <h1 className="font-display font-black text-4xl tracking-tighter">Bank Accounts</h1>
          <p className="text-zinc-600 mt-2">Multiple bank details for invoices — set one as default.</p>
        </div>
        {can(user, "invoices_write") && (
          <button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="new-bank" className="bg-zinc-900 text-white px-4 py-2.5 font-semibold flex items-center gap-2 hover:bg-zinc-800">
            <Plus size={16} /> Add Bank
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {banks.length === 0 && <div className="md:col-span-3 bg-white border border-zinc-200 p-10 text-center text-zinc-500 font-mono text-sm">[ no banks yet — add one ]</div>}
        {banks.map(b => (
          <div key={b.id} className="bg-white border border-zinc-200 p-5 hover:border-zinc-900 transition-colors">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="w-9 h-9 bg-zinc-900 text-white flex items-center justify-center"><Landmark size={16} /></div>
                <div>
                  <h3 className="font-display font-bold text-lg tracking-tight leading-none">{b.bank_name}</h3>
                  {b.branch && <p className="text-xs text-zinc-500 mt-1">{b.branch}</p>}
                </div>
              </div>
              {b.is_default && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-[#ECFDF3] text-[#027A48] border border-[#039855]"><Star size={10} /> Default</span>}
            </div>
            <div className="font-mono text-sm space-y-1 mb-4">
              <div><span className="text-zinc-500 text-xs">A/c </span>{b.account_number}</div>
              <div><span className="text-zinc-500 text-xs">IFSC </span>{b.ifsc}</div>
              <div className="text-zinc-700">{b.holder_name}</div>
              {b.upi_id && <div className="text-xs text-[#002FA7]">UPI: {b.upi_id}</div>}
            </div>
            <div className="flex justify-end gap-1 pt-3 border-t border-zinc-100">
              {can(user, "invoices_write") && <button onClick={() => { setEditing(b); setForm(b); setOpen(true); }} className="p-1.5 hover:bg-zinc-100"><Pencil size={14} /></button>}
              {can(user, "delete") && <button onClick={() => remove(b.id)} className="p-1.5 hover:bg-red-50 text-[#D92D20]"><Trash2 size={14} /></button>}
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-zinc-200 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between">
              <h2 className="font-display font-bold text-xl tracking-tight">{editing ? "Edit Bank" : "Add Bank"}</h2>
              <button onClick={() => setOpen(false)}><X size={18} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div>
                <label className="label-caps block mb-2">Bank Name *</label>
                <input required value={form.bank_name} onChange={e => setForm({ ...form, bank_name: e.target.value })} data-testid="bank-name" className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900" />
              </div>
              <div>
                <label className="label-caps block mb-2">Account Number *</label>
                <input required value={form.account_number} onChange={e => setForm({ ...form, account_number: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 font-mono focus:outline-none focus:border-zinc-900" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-caps block mb-2">IFSC *</label>
                  <input required value={form.ifsc} onChange={e => setForm({ ...form, ifsc: e.target.value.toUpperCase() })} className="w-full border border-zinc-300 px-3 py-2 font-mono focus:outline-none focus:border-zinc-900" />
                </div>
                <div>
                  <label className="label-caps block mb-2">Branch</label>
                  <input value={form.branch || ""} onChange={e => setForm({ ...form, branch: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900" />
                </div>
              </div>
              <div>
                <label className="label-caps block mb-2">Account Holder *</label>
                <input required value={form.holder_name} onChange={e => setForm({ ...form, holder_name: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900" />
              </div>
              <div>
                <label className="label-caps block mb-2">UPI ID</label>
                <input value={form.upi_id || ""} onChange={e => setForm({ ...form, upi_id: e.target.value })} placeholder="name@bank" className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-zinc-900" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!form.is_default} onChange={e => setForm({ ...form, is_default: e.target.checked })} className="w-4 h-4 accent-zinc-900" />
                <span className="text-sm">Set as default bank for invoices</span>
              </label>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-zinc-300 font-semibold">Cancel</button>
                <button type="submit" data-testid="save-bank" className="bg-zinc-900 text-white px-4 py-2 font-semibold">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
